import type { TruckGatepassFormData } from "@features/gatepass/schema/truckGatepassFormSchema";
import type { UseFormReturn } from "react-hook-form";

const toLocalDateOnlyKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const toDateOnlyKey = (value: unknown): string => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toLocalDateOnlyKey(value);
  }
  const asString = String(value ?? "").trim();
  if (!asString) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return asString;
  const parsed = new Date(asString);
  return Number.isNaN(parsed.getTime()) ? "" : toLocalDateOnlyKey(parsed);
};

type EntryDateField = "expectedEntryDate" | "jobStartDate";

const toNormalizedString = (value: unknown): string => String(value ?? "").trim();

export const hasActiveHazardCargo = (
  cargos: Array<{ hazardCargo?: boolean; cargo?: unknown }> | undefined,
): boolean =>
  (cargos ?? []).some(
    (cargo) => cargo?.hazardCargo === true && Boolean(toNormalizedString(cargo?.cargo)),
  );

export const hasHazardCargoWithMultipleTypes = (
  cargos: Array<{ hazardCargo?: boolean; cargo?: unknown }> | undefined,
): boolean => hasActiveHazardCargo(cargos) && (cargos?.length ?? 0) > 1;

export const syncTruckEntryDateField = (
  formData: UseFormReturn<TruckGatepassFormData>,
  updateFormData: (key: string, value: unknown) => void,
  sourceField: EntryDateField,
  sourceValue: unknown,
) => {
  const sourceKey = toDateOnlyKey(sourceValue);
  if (!sourceKey) return;

  const targetField: EntryDateField =
    sourceField === "expectedEntryDate" ? "jobStartDate" : "expectedEntryDate";
  const targetValue = formData.getValues(targetField);
  if (toDateOnlyKey(targetValue) === sourceKey) return;

  formData.setValue(targetField, sourceValue as never, {
    shouldDirty: true,
    shouldValidate: true,
  });
  updateFormData(targetField, sourceValue);
};
